import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, ChevronDown, ChevronUp, X, Info, Calculator, TrendingUp, Download, FileSpreadsheet, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import SimpleOwnerSelector from '../components/common/SimpleOwnerSelector';
import { masterSupabase } from '../lib/supabase-master';
import * as XLSX from 'xlsx';

// Tooltip Component
const Tooltip = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!content) return children;

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className={`absolute z-50 ${positionClasses[position]} pointer-events-none`}>
          <div className="bg-gray-900 dark:bg-slate-800 text-white text-xs rounded-lg p-3 shadow-lg max-w-xs border border-gray-600">
            <div className="whitespace-pre-line">{content}</div>
            {/* Arrow */}
            <div className={`absolute w-2 h-2 bg-gray-900 dark:bg-slate-800 border border-gray-600 transform rotate-45 ${
              position === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1 border-t-0 border-l-0' :
              position === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1 border-b-0 border-r-0' :
              position === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1 border-l-0 border-b-0' :
              'right-full top-1/2 -translate-y-1/2 -mr-1 border-r-0 border-t-0'
            }`}></div>
          </div>
        </div>
      )}
    </div>
  );
};


// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, pageSize, onPageSizeChange }) => {
  if (totalPages <= 1) return null;
  
  const pages = [];
  const maxVisible = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
  
  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }
  
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700 dark:text-gray-300">Einträge pro Seite:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-slate-800 text-sm"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2">...</span>}
          </>
        )}
        
        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 border rounded ${
              page === currentPage
                ? 'bg-blue-500 text-white border-blue-500'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2">...</span>}
            <button
              onClick={() => onPageChange(totalPages)}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {totalPages}
            </button>
          </>
        )}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      
      <div className="text-sm text-gray-700 dark:text-gray-300">
        Seite {currentPage} von {totalPages}
      </div>
    </div>
  );
};

const SteuernPage = () => {
  // States
  const [entries, setEntries] = useState([]);
  const [propertyData, setPropertyData] = useState(null);
  const [summary, setSummary] = useState({ income: 0, expenses: 0, profit: 0, count: 0 });
  const [specificKPIs, setSpecificKPIs] = useState({ 
    salary: { total: 0, christian: 0, torsten: 0 },
    privateLoan: { total: 0, christian: 0, torsten: 0 },
    bankLoan: { current: 0, zinsen: 0, tilgung: 0 },
    otherLoans: { total: 0, proEstate: 0, events: 0 }
  });
  const [rentalKPIs, setRentalKPIs] = useState({
    totalRentalIncome: 0,
    totalRentalExpenses: 0,
    netRentalIncome: 0,
    tenantBreakdown: {}
  });
  const [rentVariance, setRentVariance] = useState({
    total_soll: 0,
    total_ist: 0,
    total_variance: 0,
    tenant_count: 0,
    tenants: [],
    summary: {
      critical_count: 0,
      warning_count: 0,
      good_count: 0
    }
  });
  const [calculationDetails, setCalculationDetails] = useState({});
  const [rentalFilter, setRentalFilter] = useState(''); // 'income', 'expense', or ''
  const [tenantVarianceFilter, setTenantVarianceFilter] = useState(''); // 'critical', 'warning', 'good', or ''
  
  // New click filter states
  const [clickFilter, setClickFilter] = useState({
    type: '', // 'tenant', 'income', 'expense', 'salary', 'loan', 'critical_tenants'
    value: '', // specific tenant name or filter value
    label: '' // display label for the filter
  });
  
  const [selectedOwner, setSelectedOwner] = useState('acb36fe0-8168-45e3-b010-043f2a3d2026'); // HiSa UUID
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedYear, setSelectedYear] = useState('all');
  const [totalCount, setTotalCount] = useState(0);
  const [latestDataMonth, setLatestDataMonth] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [tenantSollValues, setTenantSollValues] = useState({});
  
  // NEW: Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });


  // Tenant name mapping for display purposes
  const tenantNameMapping = {
    'F X GmbH': 'FitX Deutschland GmbH',
    'P M GmbH': 'Penny Markt GmbH',
    'R C GmbH': 'Riddle & Code GmbH',
    'H I GmbH': 'Hassmann Immobilien',
    'FitX Deutschland GmbH': 'FitX Deutschland GmbH',
    'Penny Markt GmbH': 'Penny Markt GmbH',
    'Riddle & Code GmbH': 'Riddle & Code GmbH',
    'Hassmann Immobilien': 'Hassmann Immobilien'
  };

  // Get display name for tenant
  const getDisplayTenantName = (apiName) => {
    return tenantNameMapping[apiName] || apiName;
  };

  // Click filter handlers
  const handleClickFilter = (type, value, label) => {
    // Toggle off if same filter is clicked again
    if (clickFilter.type === type && clickFilter.value === value) {
      setClickFilter({ type: '', value: '', label: '' });
    } else {
      setClickFilter({ type, value, label });
    }
  };

  const clearClickFilter = () => {
    setClickFilter({ type: '', value: '', label: '' });
  };

  // Fetch tenant Soll values from Supabase
  const fetchTenantSollValues = async () => {
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) {
        console.log('No auth session for tenant data');
        return {};
      }

      const currentDate = new Date().toISOString().split('T')[0];
      
      const { data: tenants, error } = await masterSupabase
        .from('tenants')
        .select('name, monthly_rent, lease_start, lease_end, unit_identifier')
        .lte('lease_start', currentDate)
        .or(`lease_end.gte.${currentDate},lease_end.is.null`);

      if (error) {
        console.error('Error fetching tenants:', error);
        return {};
      }

      // Convert to expected format with name normalization
      const sollValues = {};
      tenants?.forEach(tenant => {
        const supabaseName = tenant.name?.trim();
        const monthlyRent = parseFloat(tenant.monthly_rent || 0);
        
        if (supabaseName && monthlyRent > 0) {
          // Normalize abbreviated names to full DATEV names
          let normalizedName = supabaseName;
          
          // Direct mapping for abbreviated names
          const nameMapping = {
            'F X GmbH': 'FitX Deutschland GmbH',
            'P M GmbH': 'Penny Markt GmbH', 
            'R C GmbH': 'Riddle & Code GmbH',
            'H I GmbH': 'Hassmann Immobilien',
            'Fin Co GbR': 'FitX Deutschland GmbH' // From the data we saw
          };
          
          if (nameMapping[supabaseName]) {
            normalizedName = nameMapping[supabaseName];
          } else {
            // Fuzzy matching for partial names
            const lowerName = supabaseName.toLowerCase();
            if (lowerName.includes('fitx') || lowerName.includes('f x') || lowerName.includes('fin')) {
              normalizedName = 'FitX Deutschland GmbH';
            } else if (lowerName.includes('penny') || lowerName.includes('p m')) {
              normalizedName = 'Penny Markt GmbH';
            } else if (lowerName.includes('riddle') || lowerName.includes('r c')) {
              normalizedName = 'Riddle & Code GmbH';
            } else if (lowerName.includes('hassmann') || lowerName.includes('h i')) {
              normalizedName = 'Hassmann Immobilien';
            }
          }
          
          sollValues[normalizedName] = monthlyRent;
          console.log(`Tenant Soll: ${supabaseName} -> ${normalizedName} -> €${monthlyRent}`);
        }
      });

      setTenantSollValues(sollValues);
      return sollValues;
    } catch (error) {
      console.error('Error fetching tenant Soll values:', error);
      return {};
    }
  };

  // Generate tooltip content for KPIs
  const generateKPITooltip = (type, data) => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const currentYear = new Date().getFullYear();
    
    switch (type) {
      case 'income':
        const incomeEntries = entries.filter(e => e.amount < 0).length; // CORRECTED: Negative = Income (Haben)
        const avgIncome = incomeEntries > 0 ? data / incomeEntries : 0;
        return `📊 Einnahmen-Details:
• Anzahl Buchungen: ${incomeEntries}
• Durchschnitt: ${formatCurrency(avgIncome)}
• Zeitraum: ${selectedYear === 'all' ? 'Gesamt' : selectedYear}
• Größte Einnahmen: Mieteinnahmen
• Berechnung: Summe aller positiven Beträge (Soll-Buchungen)
• DATEV-Standard: Positive Werte = Ausgaben (Haben-Buchungen)
• DATEV-Standard: Negative Werte = Einnahmen

💡 Klicken zum Filtern der Tabelle`;

      case 'expenses':
        const expenseEntries = entries.filter(e => e.amount > 0).length; // CORRECTED: Positive = Expenses (Soll)
        const avgExpense = expenseEntries > 0 ? Math.abs(data) / expenseEntries : 0;
        return `📊 Ausgaben-Details:
• Anzahl Buchungen: ${expenseEntries}
• Durchschnitt: ${formatCurrency(avgExpense)}
• Zeitraum: ${selectedYear === 'all' ? 'Gesamt' : selectedYear}
• Größte Ausgaben: Darlehen & Gehälter
• Berechnung: Summe aller positiven Beträge (Soll-Buchungen)
• DATEV-Standard: Positive Werte = Ausgaben

💡 Klicken zum Filtern der Tabelle`;

      case 'profit':
        const profitMargin = data !== 0 ? ((data / (data + Math.abs(summary.expenses))) * 100).toFixed(1) : 0;
        return `📊 Gewinn-Details:
• Gewinnmarge: ${profitMargin}%
• Einnahmen: ${formatCurrency(summary.income)}
• Ausgaben: ${formatCurrency(summary.expenses)}
• Berechnung: Einnahmen - Ausgaben
• Status: ${data >= 0 ? '✅ Positiv' : '❌ Verlust'}`;

      case 'bookings':
        return `📊 Buchungs-Details:
• Gesamtanzahl: ${data}
• Zeitraum: ${selectedYear === 'all' ? 'Gesamt' : selectedYear}
• Letzter Monat: ${getLatestMonth()}
• Durchschnitt/Monat: ${Math.round(data / Math.max(currentMonth, 1))}
• Quelle: DATEV-Import`;

      case 'salary':
        const salaryEntries = entries.filter(e => 
          e.description?.toLowerCase().includes('gehalt') || 
          e.description?.toLowerCase().includes('lohn')
        ).length;
        return `💰 Gehalt-Details:
• Buchungen: ${salaryEntries}x Gehaltsauszahlungen
• Christian: ${formatCurrency(data.christian)}
• Torsten: ${formatCurrency(data.torsten)}
• Monatlich: ${formatCurrency(data.total)}
• Erwartung: 2x Gehälter/Monat
• ${salaryEntries < currentMonth * 2 ? '⚠️ Weniger als erwartet' : '✅ Vollständig'}

💡 Klicken zum Filtern der Tabelle`;

      case 'privateLoan':
        const loanEntries = entries.filter(e => 
          e.description?.toLowerCase().includes('hiss') && 
          (e.description?.toLowerCase().includes('zins') || e.description?.toLowerCase().includes('tilgung'))
        ).length;
        return `🏦 Privatdarlehen-Details:
• Buchungen: ${loanEntries}x Zins & Tilgung
• Christian: ${formatCurrency(data.christian)}
• Torsten: ${formatCurrency(data.torsten)}
• Gesamt: ${formatCurrency(data.total)}
• Erwartung: Regelmäßige Zahlungen
• ${loanEntries < currentMonth ? '⚠️ Unregelmäßig' : '✅ Pünktlich'}

💡 Klicken zum Filtern der Tabelle`;

      case 'bankLoan':
        const bankEntries = entries.filter(e => 
          e.description?.toLowerCase().includes('spk') || 
          e.description?.toLowerCase().includes('darlehen')
        ).length;
        return `🏛️ Bankdarlehen-Details:
• Buchungen: ${bankEntries}x Bankzahlungen
• Zinsen: ${formatCurrency(data.zinsen)}
• Tilgung: ${formatCurrency(data.tilgung)}
• Aktueller Stand: ${formatCurrency(data.current)}
• Erwartung: Monatliche Zahlungen
• ${bankEntries < currentMonth ? '⚠️ Fehlende Zahlungen' : '✅ Vollständig'}

💡 Klicken zum Filtern der Tabelle`;

      default:
        return null;
    }
  };

  // Generate tooltip content for tenant breakdown
  const generateTenantTooltip = (tenant, amount) => {
    const tenantEntries = entries.filter(e => 
      e.description?.toLowerCase().includes(tenant.toLowerCase().split(' ')[0])
    );
    
    const currentMonth = new Date().getMonth() + 1;
    const expectedPayments = currentMonth; // Expected monthly payments
    const actualPayments = tenantEntries.length;
    const avgPayment = actualPayments > 0 ? amount / actualPayments : 0;
    const sollAmount = tenantSollValues[tenant] || 0;
    const expectedTotal = sollAmount * expectedPayments;
    
    return `🏢 ${tenant} - Details:
• Buchungen: ${actualPayments}x Zahlungen
• Durchschnitt: ${formatCurrency(avgPayment)}
• Soll (Monat): ${formatCurrency(sollAmount)}
• Erwartet (${expectedPayments} Monate): ${formatCurrency(expectedTotal)}
• Tatsächlich: ${formatCurrency(amount)}
• Abweichung: ${formatCurrency(amount - expectedTotal)}
• Status: ${actualPayments >= expectedPayments ? '✅ Vollständig' : '⚠️ Fehlend'}

📋 Letzte Buchungen:
${tenantEntries.slice(0, 3).map(e => 
  `• ${formatDate(e.date)}: ${formatCurrency(e.amount)}`
).join('\n')}

💡 Klicken zum Filtern der Tabelle`;
  };

  // Generate tooltip content for Soll-Ist comparison
  const generateVarianceTooltip = (type, data) => {
    switch (type) {
      case 'total_variance':
        const variancePercent = data.total_soll !== 0 ? ((data.total_variance / data.total_soll) * 100).toFixed(1) : 0;
        return `📊 Gesamt-Abweichung Details:
• Soll-Betrag: ${formatCurrency(data.total_soll)}
• Ist-Betrag: ${formatCurrency(data.total_ist)}
• Abweichung: ${formatCurrency(data.total_variance)}
• Prozentual: ${variancePercent}%
• Mieter: ${data.tenant_count}
• Berechnung: Ist - Soll
• ${data.total_variance < 0 ? '❌ Rückstände' : '✅ Überzahlung'}`;

      case 'critical':
        return `🔴 Kritische Mieter (${data}):
• Rückstand: > €1.000
• Sofortige Maßnahmen erforderlich
• Mahnverfahren einleiten
• Rechtliche Schritte prüfen
• Regelmäßige Überwachung

💡 Klicken zum Filtern der Tabelle`;

      case 'warning':
        return `🟡 Warnungen (${data}):
• Rückstand: €100 - €1.000
• Mahnung versenden
• Zahlungsplan anbieten
• Monatliche Kontrolle
• Präventive Maßnahmen

💡 Klicken zum Filtern der Tabelle`;

      case 'good':
        return `🟢 Pünktliche Mieter (${data}):
• Keine Rückstände
• Zahlungen erfolgen termingerecht
• Gute Zahlungsmoral
• Keine Maßnahmen erforderlich
• Weiter beobachten

💡 Klicken zum Filtern der Tabelle`;

      default:
        return null;
    }
  };

  // Load data when filters change
  useEffect(() => {
    fetchData();
  }, [searchTerm, selectedYear, startDate, endDate, selectedAccount, sortField, sortDirection, selectedOwner]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get Supabase token
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) {
        throw new Error('No authentication session');
      }

      const token = session.access_token;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedYear && selectedYear !== 'all') params.append('year', selectedYear);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedAccount) params.append('account', selectedAccount);
      if (sortField) params.append('sort_field', sortField);
      if (sortDirection) params.append('sort_direction', sortDirection);
      if (selectedOwner && selectedOwner !== 'all') params.append('owner_id', selectedOwner);

      // Fetch main entries
      const response = await fetch(`/api/entries?${params.toString()}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
        setSummary(data.summary || { income: 0, expenses: 0, profit: 0, count: 0 });
        setTotalCount(data.total_count || 0);
        setLatestDataMonth(data.latest_data_month || '');
        setCalculationDetails(data.calculation_details || {});
      }

      // Fetch KPI summary from new endpoint
      try {
        const kpiResponse = await fetch(`/api/kpi-summary?year=${selectedYear}&owner_id=${selectedOwner}`, {
          headers
        });
        if (kpiResponse.ok) {
          const kpiData = await kpiResponse.json();
          const kpiSummary = kpiData.kpi_summary || {};
          setSpecificKPIs({
            salary: { 
              total: kpiSummary.salary?.total || 0, 
              christian: kpiSummary.salary?.christian || 0, 
              torsten: kpiSummary.salary?.torsten || 0 
            },
            privateLoan: { 
              total: kpiSummary.private_loans?.total || 0, 
              christian: kpiSummary.private_loans?.christian || 0, 
              torsten: kpiSummary.private_loans?.torsten || 0 
            },
            bankLoan: { 
              current: kpiSummary.bank_loans?.total || 0, 
              zinsen: kpiSummary.bank_loans?.zinsen || 0, 
              tilgung: kpiSummary.bank_loans?.tilgung || 0 
            },
            otherLoans: { 
              total: kpiSummary.other_loans?.total || 0, 
              proEstate: kpiSummary.other_loans?.proEstate || 0, 
              events: kpiSummary.other_loans?.events || 0 
            }
          });
        }
      } catch (error) {
        console.error('Error fetching KPI summary:', error);
        // Fallback to empty KPIs
        setSpecificKPIs({
          salary: { total: 0, christian: 0, torsten: 0 },
          privateLoan: { total: 0, christian: 0, torsten: 0 },
          bankLoan: { current: 0, zinsen: 0, tilgung: 0 },
          otherLoans: { total: 0, proEstate: 0, events: 0 }
        });
      }

      // Fetch rental KPIs from API
      try {
        const rentalResponse = await fetch(`/api/rental-kpis?year=${selectedYear}&owner_id=${selectedOwner}`, {
          headers
        });
        if (rentalResponse.ok) {
          const rentalData = await rentalResponse.json();
          setRentalKPIs(rentalData.rental_kpis || {
            totalRentalIncome: 0,
            totalRentalExpenses: 0,
            netRentalIncome: 0,
            tenantBreakdown: {}
          });
        }
      } catch (error) {
        console.error('Error fetching rental KPIs:', error);
      }

      // Fetch tenant Soll values from Supabase first
      const sollValues = await fetchTenantSollValues();

      // Fetch rent variance for Mahnwesen (Soll-Ist-Abgleich) with Soll values
      try {
        const sollParams = new URLSearchParams();
        Object.entries(sollValues).forEach(([tenant, amount]) => {
          sollParams.append('soll_values', `${tenant}:${amount}`);
        });
        
        const varianceUrl = `/api/rental-analysis?year=${selectedYear}&owner_id=${selectedOwner}&${sollParams.toString()}`;
        const varianceResponse = await fetch(varianceUrl, { headers });
        
        if (varianceResponse.ok) {
          const varianceData = await varianceResponse.json();
          console.log('Rental Analysis API Response:', varianceData);
          
          // Process the variance data to ensure proper structure
          const processedVarianceData = {
            total_soll: varianceData.total_soll || 0,
            total_ist: varianceData.total_ist || 0,
            total_variance: varianceData.total_variance || 0,
            tenant_count: varianceData.tenant_count || 0,
            tenants: (varianceData.tenants || []).map(tenant => ({
              tenant: tenant.tenant || 'Unbekannt',
              property: tenant.property || '-',
              soll: tenant.soll || 0,
              ist: tenant.ist || 0,
              variance: tenant.variance || 0,
              status: tenant.status || 'unknown'
            })),
            summary: {
              critical_count: varianceData.summary?.critical_count || 0,
              warning_count: varianceData.summary?.warning_count || 0,
              good_count: varianceData.summary?.good_count || 0
            }
          };
          
          setRentVariance(processedVarianceData);
        }
      } catch (error) {
        console.error('Error fetching rent variance:', error);
        setRentVariance({
          total_soll: 0,
          total_ist: 0,
          total_variance: 0,
          tenant_count: 0,
          tenants: [],
          summary: { critical_count: 0, warning_count: 0, good_count: 0 }
        });
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Upload handlers
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    setUploadStatus('Uploading...');

    try {
      // Get Supabase token
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session) {
        throw new Error('No authentication session');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/datev/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadStatus(`Upload successful! ${result.entries_count || 0} entries processed.`);
        // Refresh data after upload
        setTimeout(() => {
          fetchData();
          setUploadStatus('');
        }, 2000);
      } else {
        const error = await response.json();
        setUploadStatus(`Upload failed: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  // NEW: Export functions
  const exportToExcel = () => {
    try {
      const exportData = filteredEntries.map(entry => ({
        'Datum': formatDate(entry.date),
        'Konto': entry.account,
        'Beschreibung': entry.description,
        'Betrag': entry.amount,
        'Kategorie': entry.category || '',
        'Mieter': entry.tenant || ''
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Buchungen");
      
      const summaryData = [
        { 'KPI': 'Einnahmen', 'Betrag': summary.income },
        { 'KPI': 'Ausgaben', 'Betrag': summary.expenses },
        { 'KPI': 'Gewinn', 'Betrag': summary.profit },
        { 'KPI': 'Anzahl Buchungen', 'Betrag': summary.count }
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Zusammenfassung");
      
      XLSX.writeFile(workbook, `buchungen_${selectedYear}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Fehler beim Excel-Export');
    }
  };
  
  const exportToCSV = () => {
    try {
      const headers = ['Datum', 'Konto', 'Beschreibung', 'Betrag', 'Kategorie', 'Mieter'];
      const csvData = filteredEntries.map(entry => [
        formatDate(entry.date),
        entry.account,
        entry.description,
        entry.amount,
        entry.category || '',
        entry.tenant || ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `buchungen_${selectedYear}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      console.error('CSV export error:', error);
      alert('Fehler beim CSV-Export');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSelectedAccount('');
    setSelectedYear('all');
    setRentalFilter('');
    clearClickFilter();
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    return months[monthNumber - 1] || 'Unbekannt';
  };

  const getLatestMonth = () => {
    if (!latestDataMonth) return 'Aktuell';
    const [year, month] = latestDataMonth.split('-');
    return getMonthName(parseInt(month));
  };

  // Enhanced filter function that includes click filters
  const filteredEntries = entries.filter(entry => {
    // Apply existing rental filter
    if (rentalFilter) {
      const description = entry.description?.toLowerCase() || '';
      const isRentalIncome = description.includes('penny') || description.includes('fitx') || 
                            description.includes('miete') || description.includes('stellplatz');
      const isRentalExpense = description.includes('deffner');
      
      if (rentalFilter === 'income' && !isRentalIncome) return false;
      if (rentalFilter === 'expense' && !isRentalExpense) return false;
    }

    // Apply click filters
    if (clickFilter.type) {
      const description = entry.description?.toLowerCase() || '';
      
      switch (clickFilter.type) {
        case 'tenant':
          // Filter by specific tenant
          const tenantKeywords = clickFilter.value.toLowerCase().split(' ');
          const hasKeyword = tenantKeywords.some(keyword => 
            description.includes(keyword.toLowerCase())
          );
          if (!hasKeyword) return false;
          break;
          
        case 'income':
          if (entry.amount <= 0) return false;
          break;
          
        case 'expense':
          if (entry.amount >= 0) return false;
          break;
          
        case 'salary':
          if (!description.includes('gehalt') && !description.includes('lohn')) return false;
          break;
          
        case 'loan':
          const isLoan = description.includes('hiss') || description.includes('spk') || 
                       description.includes('darlehen') || description.includes('zins') || 
                       description.includes('tilgung');
          if (!isLoan) return false;
          break;
          
        case 'critical_tenants':
          // Filter by critical tenants from variance data
          const criticalTenants = rentVariance.tenants
            .filter(t => (t.variance || 0) < -1000)
            .map(t => t.tenant?.toLowerCase() || '');
          
          const isCriticalTenant = criticalTenants.some(tenant => {
            if (!tenant) return false;
            const tenantWords = tenant.split(' ');
            return tenantWords.some(word => description.includes(word));
          });
          
          if (!isCriticalTenant) return false;
          break;
          
        default:
          break;
      }
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Steuern & Buchhaltung bis August 2025
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Übersicht aller DATEV-Buchungen und Finanzanalysen
            </p>
          </div>

      {/* NEW: Export Buttons */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Excel Export
        </button>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <FileText className="w-4 h-4" />
          CSV Export
        </button>
      </div>

          <SimpleOwnerSelector 
            selectedOwner={selectedOwner}
            onOwnerChange={setSelectedOwner}
          />
        </div>

        {/* Active Click Filter Display */}
        {clickFilter.type && (
          <div className="mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Aktiver Filter:</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                {clickFilter.label}
                <button
                  onClick={clearClickFilter}
                  className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          </div>
        )}

        {/* Main KPI Cards with Click Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Tooltip content={generateKPITooltip('income', summary.income)}>
            <div 
              className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-all cursor-pointer ${
                clickFilter.type === 'income' ? 'ring-2 ring-green-500 shadow-lg' : ''
              }`}
              onClick={() => handleClickFilter('income', 'income', 'Nur Einnahmen')}
            >
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Einnahmen</p>
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Gesamtsumme</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(summary.income)}
                </p>
                {clickFilter.type === 'income' && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">Aktiver Filter</p>
                )}
              </div>
            </div>
          </Tooltip>

          <Tooltip content={generateKPITooltip('expenses', summary.expenses)}>
            <div 
              className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-all cursor-pointer ${
                clickFilter.type === 'expense' ? 'ring-2 ring-red-500 shadow-lg' : ''
              }`}
              onClick={() => handleClickFilter('expense', 'expense', 'Nur Ausgaben')}
            >
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Ausgaben</p>
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Gesamtsumme</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(summary.expenses)}
                </p>
                {clickFilter.type === 'expense' && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">Aktiver Filter</p>
                )}
              </div>
            </div>
          </Tooltip>

          <Tooltip content={generateKPITooltip('profit', summary.profit)}>
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-help">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gewinn</p>
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Netto-Ergebnis</p>
                <p className={`text-2xl font-bold ${summary.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(summary.profit)}
                </p>
              </div>
            </div>
          </Tooltip>

          <Tooltip content={generateKPITooltip('bookings', summary.count)}>
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-help">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Buchungen</p>
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Anzahl Einträge</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {summary.count}
                </p>
              </div>
            </div>
          </Tooltip>
        </div>

        {/* Specific KPI Cards with Click Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Gehalt Card */}
          <Tooltip content={generateKPITooltip('salary', specificKPIs.salary)}>
            <div 
              className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-all cursor-pointer ${
                clickFilter.type === 'salary' ? 'ring-2 ring-purple-500 shadow-lg' : ''
              }`}
              onClick={() => handleClickFilter('salary', 'salary', 'Gehaltsbuchungen')}
            >
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gehalt</p>
                  <Calculator className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Monatlich</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-3">
                  {formatCurrency(specificKPIs.salary?.total || 0)}
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Christian:</span>
                    <span className="font-medium">{formatCurrency(specificKPIs.salary?.christian || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Torsten:</span>
                    <span className="font-medium">{formatCurrency(specificKPIs.salary?.torsten || 0)}</span>
                  </div>
                </div>
                {clickFilter.type === 'salary' && (
                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-2 font-medium">Aktiver Filter</p>
                )}
              </div>
            </div>
          </Tooltip>

          {/* Privatdarlehen Card */}
          <Tooltip content={generateKPITooltip('privateLoan', specificKPIs.privateLoan)}>
            <div 
              className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-all cursor-pointer ${
                clickFilter.type === 'loan' ? 'ring-2 ring-orange-500 shadow-lg' : ''
              }`}
              onClick={() => handleClickFilter('loan', 'loan', 'Darlehen & Zinsen')}
            >
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Privatdarlehen</p>
                  <TrendingUp className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Zins & Tilgung</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-3">
                  {formatCurrency(specificKPIs.privateLoan?.total || 0)}
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Christian:</span>
                    <span className="font-medium">{formatCurrency(specificKPIs.privateLoan?.christian || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Torsten:</span>
                    <span className="font-medium">{formatCurrency(specificKPIs.privateLoan?.torsten || 0)}</span>
                  </div>
                </div>
                {clickFilter.type === 'loan' && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium">Aktiver Filter</p>
                )}
              </div>
            </div>
          </Tooltip>

          {/* Bankdarlehen Card */}
          <Tooltip content={generateKPITooltip('bankLoan', specificKPIs.bankLoan)}>
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-help">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bankdarlehen</p>
                  <Info className="w-4 h-4 ml-1 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Aktueller Stand</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400 mb-3">
                  {formatCurrency(specificKPIs.bankLoan?.current || 0)}
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Zinsen:</span>
                    <span className="font-medium">{formatCurrency(specificKPIs.bankLoan?.zinsen || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tilgung:</span>
                    <span className="font-medium">{formatCurrency(specificKPIs.bankLoan?.tilgung || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </Tooltip>

          {/* Sonstige Darlehen Card */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Sonstige Darlehen</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Zins & Tilgung</p>
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-3">
                {formatCurrency(specificKPIs.otherLoans?.total || 0)}
              </p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Pro Estate:</span>
                  <span className="font-medium">{formatCurrency(specificKPIs.otherLoans?.proEstate || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Events:</span>
                  <span className="font-medium">{formatCurrency(specificKPIs.otherLoans?.events || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Immobilien-Erträge Section with Click Filters */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Immobilien-Erträge</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Mieteinnahmen Card */}
            <div 
              className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setRentalFilter(rentalFilter === 'income' ? '' : 'income')}
            >
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Mieteinnahmen</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Gesamtsumme</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(rentalKPIs.totalRentalIncome)}
                </p>
                {rentalFilter === 'income' && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                    ✓ Filter aktiv
                  </p>
                )}
              </div>
            </div>

            {/* Mietausgaben Card */}
            <div 
              className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setRentalFilter(rentalFilter === 'expense' ? '' : 'expense')}
            >
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Mietausgaben</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Gesamtsumme</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(rentalKPIs.totalRentalExpenses)}
                </p>
                {rentalFilter === 'expense' && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    ✓ Filter aktiv
                  </p>
                )}
              </div>
            </div>

            {/* Netto-Ertrag Card */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-6">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Netto-Ertrag</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Immobilien</p>
                <p className={`text-2xl font-bold ${rentalKPIs.netRentalIncome >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(rentalKPIs.netRentalIncome)}
                </p>
              </div>
            </div>

            {/* Mieter-Anzahl Card */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-6">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Mieter</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Aktive Verträge</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {Object.keys(rentalKPIs.tenantBreakdown).length}
                </p>
              </div>
            </div>
          </div>

          {/* Tenant Breakdown with Click Filters */}
          {Object.keys(rentalKPIs.tenantBreakdown).length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Mieter-Aufschlüsselung</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(rentalKPIs.tenantBreakdown).map(([tenant, amount]) => {
                  // Get calculation details for this tenant
                  const tenantDetails = rentalKPIs.calculationDetails?.tenantDetails?.[tenant];
                  const countedEntries = tenantDetails?.entries?.length || 0;
                  const excludedEntries = tenantDetails?.excluded?.length || 0;
                  const isActive = clickFilter.type === 'tenant' && clickFilter.value === tenant;
                  
                  return (
                    <Tooltip key={tenant} content={generateTenantTooltip(tenant, amount)}>
                      <div 
                        className={`bg-gray-50 dark:bg-slate-800 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all cursor-pointer ${
                          isActive ? 'ring-2 ring-blue-500 shadow-lg' : ''
                        }`}
                        onClick={() => handleClickFilter('tenant', tenant, `Mieter: ${tenant}`)}
                      >
                        <p className="font-medium text-gray-900 dark:text-white mb-2 text-sm">{tenant}</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Einnahmen:</span>
                            <span className="font-medium text-green-600">{formatCurrency(amount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Ausgaben:</span>
                            <span className="font-medium text-red-600">{formatCurrency(0)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1 border-gray-300 dark:border-gray-600">
                            <span className="text-gray-600 dark:text-gray-400">Netto:</span>
                            <span className="font-medium text-green-600">{formatCurrency(amount)}</span>
                          </div>
                          {tenantDetails && (
                            <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-500">✅ Gezählt:</span>
                                <span className="text-gray-700 dark:text-gray-300">{countedEntries}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-500">❌ Ausgeschlossen:</span>
                                <span className="text-gray-700 dark:text-gray-300">{excludedEntries}</span>
                              </div>
                            </div>
                          )}
                          {isActive && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 font-medium">Aktiver Filter</p>
                          )}
                        </div>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Soll-Ist-Abgleich Mahnwesen with Click Filters */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Soll-Ist-Abgleich Mahnwesen</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Gesamt-Abweichung */}
            <Tooltip content={generateVarianceTooltip('total_variance', rentVariance)}>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-6 cursor-help hover:shadow-md transition-shadow">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Gesamt-Abweichung</p>
                    <Info className="w-4 h-4 ml-1 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Soll vs. Ist</p>
                  <p className={`text-2xl font-bold ${rentVariance.total_variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(rentVariance.total_variance)}
                  </p>
                </div>
              </div>
            </Tooltip>

            {/* Kritische Mieter */}
            <Tooltip content={generateVarianceTooltip('critical', rentVariance.summary.critical_count)}>
              <div 
                className={`bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
                  tenantVarianceFilter === 'critical' || clickFilter.type === 'critical_tenants' ? 'ring-2 ring-red-500 shadow-lg' : ''
                }`}
                onClick={() => {
                  if (tenantVarianceFilter === 'critical') {
                    setTenantVarianceFilter('');
                  } else {
                    setTenantVarianceFilter('critical');
                  }
                  handleClickFilter('critical_tenants', 'critical', 'Kritische Mieter');
                }}
              >
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Kritische Mieter</p>
                    <Info className="w-4 h-4 ml-1 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Rückstand über 1.000€</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    🔴 {rentVariance.summary.critical_count}
                  </p>
                  {(tenantVarianceFilter === 'critical' || clickFilter.type === 'critical_tenants') && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">Aktiver Filter</p>
                  )}
                </div>
              </div>
            </Tooltip>

            {/* Warnungen */}
            <Tooltip content={generateVarianceTooltip('warning', rentVariance.summary.warning_count)}>
              <div 
                className={`bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
                  tenantVarianceFilter === 'warning' ? 'ring-2 ring-yellow-500 shadow-lg' : ''
                }`}
                onClick={() => setTenantVarianceFilter(tenantVarianceFilter === 'warning' ? '' : 'warning')}
              >
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Warnungen</p>
                    <Info className="w-4 h-4 ml-1 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Rückstand unter 1.000€</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    🟡 {rentVariance.summary.warning_count}
                  </p>
                  {tenantVarianceFilter === 'warning' && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 font-medium">Aktiver Filter</p>
                  )}
                </div>
              </div>
            </Tooltip>

            {/* Pünktliche Mieter */}
            <Tooltip content={generateVarianceTooltip('good', rentVariance.summary.good_count)}>
              <div 
                className={`bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
                  tenantVarianceFilter === 'good' ? 'ring-2 ring-green-500 shadow-lg' : ''
                }`}
                onClick={() => setTenantVarianceFilter(tenantVarianceFilter === 'good' ? '' : 'good')}
              >
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pünktliche Mieter</p>
                    <Info className="w-4 h-4 ml-1 text-gray-400" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Keine Rückstände</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    🟢 {rentVariance.summary.good_count}
                  </p>
                  {tenantVarianceFilter === 'good' && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">Aktiver Filter</p>
                  )}
                </div>
              </div>
            </Tooltip>
          </div>

          {/* Detaillierte Mieter-Tabelle */}
          {rentVariance.tenants && rentVariance.tenants.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Mieter-Details</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Mieter
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Objekt
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Soll
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ist
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Abweichung
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {rentVariance.tenants
                      .filter(tenant => {
                        if (!tenantVarianceFilter) return true;
                        if (tenantVarianceFilter === 'critical') return (tenant.variance || 0) < -1000;
                        if (tenantVarianceFilter === 'warning') return (tenant.variance || 0) >= -1000 && (tenant.variance || 0) < 0;
                        if (tenantVarianceFilter === 'good') return (tenant.variance || 0) >= 0;
                        return true;
                      })
                      .map((tenant, index) => (
                      <tr 
                        key={index} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => handleClickFilter('tenant', getDisplayTenantName(tenant.tenant || 'Unbekannt'), `Mieter: ${getDisplayTenantName(tenant.tenant || 'Unbekannt')}`)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {getDisplayTenantName(tenant.tenant || 'Unbekannt')}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {tenant.property || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                          {formatCurrency(tenant.soll || 0)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                          {formatCurrency(tenant.ist || 0)}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-right font-medium ${
                          (tenant.variance || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(tenant.variance || 0)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {(tenant.variance || 0) < -1000 ? '🔴' : (tenant.variance || 0) < 0 ? '🟡' : '🟢'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* DATEV Upload Section */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">DATEV-Dateien hochladen</h3>
          
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging 
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".csv,.txt,.xlsx"
              onChange={handleFileInputChange}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="space-y-2">
                <div className="text-gray-400 dark:text-gray-500">
                  <svg className="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500">
                    DATEV-Dateien hier ablegen oder klicken
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Unterstützt: CSV, TXT, xlsx/DATEV-Dateien
                </p>
              </div>
            </label>
          </div>

          {uploadStatus && (
            <div className={`mt-4 p-3 rounded-md ${
              uploadStatus.includes('successful') 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                : uploadStatus.includes('failed')
                ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
            }`}>
              {uploadStatus}
            </div>
          )}

          <div className="mt-4 text-center">
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">
              DATEV-System ist bereit! {totalCount} Buchungen geladen.
            </p>
          </div>
        </div>

        {/* Advanced Filters */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Erweiterte Filter</h3>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              <Filter className="w-4 h-4" />
              <span>{showAdvancedFilters ? 'Ausblenden' : 'Anzeigen'}</span>
              {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Suche
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Beschreibung, Konto..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Jahr
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                >
                  <option value="all">Alle Jahre</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Von Datum
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bis Datum
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
          )}

          {(searchTerm || selectedYear !== 'all' || startDate || endDate || rentalFilter || clickFilter.type) && (
            <div className="mt-4 flex items-center space-x-2 flex-wrap">
              <span className="text-sm text-gray-600 dark:text-gray-400">Aktive Filter:</span>
              {searchTerm && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                  Suche: {searchTerm}
                </span>
              )}
              {selectedYear !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                  Jahr: {selectedYear}
                </span>
              )}
              {rentalFilter && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400">
                  Miete: {rentalFilter === 'income' ? 'Einnahmen' : 'Ausgaben'}
                </span>
              )}
              {clickFilter.type && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                  {clickFilter.label}
                  <button
                    onClick={clearClickFilter}
                    className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <X className="w-3 h-3 mr-1" />
                Alle löschen
              </button>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                DATEV-Buchungen
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {filteredEntries.length} von {entries.length} Einträgen
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('date')}
                  >
                    Datum {getSortIcon('date')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('description')}
                  >
                    Beschreibung {getSortIcon('description')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('account')}
                  >
                    Konto {getSortIcon('account')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    S/H
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Gegenkonto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Monat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Belegtyp
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('amount')}
                  >
                    Betrag {getSortIcon('amount')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      Lade Daten...
                    </td>
                  </tr>
                ) : filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      Keine Einträge gefunden
                    </td>
                  </tr>
                ) : (
                  filteredEntries.map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(entry.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {entry.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {entry.account}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {entry.debit_credit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {entry.contra_account}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {entry.month}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {entry.document_type}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                        entry.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatCurrency(entry.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SteuernPage;
